import { session } from "../auth/session";
import { getDeploymentConfig } from "./deployment-config";
import { ApiError, SignedOutError, createApiClient, statusOf } from "@jkast90/kast-common/api-client";
import { needsConnection } from "@jkast90/kast-common/offline";

export { ApiError, SignedOutError, statusOf };

// The one way to call play-api (api/). Every route is behind the shared
// Cognito authorizer and a pocket-play group check, so every call carries
// the session's bearer; refusals come back as {"detail": "..."}.

/**
 * The browser has no network, so the call was never sent. Pages show its
 * message where the cloud's data would be; nothing local depends on it.
 */
export class OfflineError extends ApiError {
  readonly needsConnection = true;
  constructor() {
    super("You're offline: the cloud needs a connection, and comes back when you are.");
  }
}

/** Whether signing in (again) is what the error needs: signed out, or a sign-in that expired. */
export const needsSignIn = (error: unknown) => error instanceof SignedOutError || statusOf(error) === 401;

/**
 * For a route that does slow work server-side (a log query, an S3 copy, a
 * fetch from GitHub). API Gateway gives up at 29 s, so waiting longer is
 * pointless.
 */
export const SLOW_CALL = { timeoutMs: 30_000 };

// The mechanics are kast-common's api-client: an expired token (a 401) or one
// from before a group change (a 403) is refreshed once, as the session
// decides, and the call sent again; nothing else is retried here (a POST may
// not be safe to repeat; the query client retries its reads). Each attempt has
// its own time limit (8 s unless `options` says otherwise); a call the caller
// cancelled rejects with that abort, untouched. This is the wording, and the
// offline rule: nothing is sent without a network, and a dropped connection
// says so.
const client = createApiClient({
  baseUrl: () => getDeploymentConfig().apiUrl,
  session,
  messages: {
    timeout: "The cloud didn't answer in time. Check your connection and try again.",
    unreachable: "The cloud is unreachable right now.",
    signedOut: "Sign in to use cloud features.",
    forbidden: "This account isn't allowed to use cloud features.",
  },
  beforeSend: () => needsConnection(() => new OfflineError()),
  onNetworkFailure: () => {
    needsConnection(() => new OfflineError());
    return undefined;
  },
});

export const apiRequest = client.request;
